import { urls } from '../../../urls';
import { useCallback, useEffect, useState } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { Box } from '@mui/material';
import { Loader } from '@/components/loader/loader';
import { formatCurrency, formatCurrencyNoUnit } from '@/lib/formatters';
import { DS_CHART_COLORS } from '@/theme/tokens';
import { SvgDonut } from '@/components/ds/charts';

// Show the top types individually and roll the long tail into one "Other" slice
// (mirrors the design donut: a handful of named types + Other), so the legend
// stays short and the card height matches its bar-chart row-mate.
const TOP_N = 6;
const OTHER_COLOR = '#C6CFDC';
// Match Top10 / Total Spending chart height (~400–420 like old MUI reporting).
const CHART_H = 420;

function DonutLegend({
  items,
  compact = false,
}: {
  items: { id: React.Key; color: string; name: string; value: string }[];
  compact?: boolean;
}) {
  const fontSize = compact ? 12 : 14;
  const swatch = compact ? 8 : 10;
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? '7px' : '10px',
      }}>
      {items.map((b) => (
        <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: compact ? '8px' : '10px' }}>
          <Box
            component="span"
            sx={{
              width: swatch,
              height: swatch,
              borderRadius: '3px',
              background: b.color,
              flex: 'none',
            }}
          />
          <Box
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: 'Lato',
              fontSize,
              color: '#3D4A5C',
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={String(b.name)}>
            {b.name}
          </Box>
          <Box
            component="span"
            sx={{
              fontFamily: 'Lato',
              fontSize,
              color: '#16233A',
              fontWeight: 900,
              flex: 'none',
            }}>
            {b.value}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

interface CostsByResourceTypeProps {
  filters?: string;
}

export default function CostsByResourceType({
  filters,
}: Readonly<CostsByResourceTypeProps>) {
  const [chartItems, setChartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cardW, setCardW] = useState(0);

  const containerRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const measure = () => setCardW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
  }, []);

  useEffect(() => {
    setLoading(true);

    fetchWrapper
      .get(urls.DevOps.getCostsByResourceType + (filters ? `?${filters}` : ''))
      .then((data) => setChartItems(data))
      .catch((error) => console.error('There was an error!', error))
      .finally(() => {
        setLoading(false);
      });
  }, [filters]);

  if (loading) {
    return <Loader />;
  }

  const rows = chartItems
    .map((item: any) => ({
      id: item.resource_type as React.Key,
      value: Number(item.cost ?? 0),
      label: item.resource_type as string,
    }))
    .sort((a, b) => b.value - a.value);

  const total = rows.reduce((sum, item) => sum + item.value, 0);

  // Top N named types + a single aggregated "Other" for the rest.
  const top = rows.slice(0, TOP_N);
  const rest = rows.slice(TOP_N);
  const restValue = rest.reduce((sum, item) => sum + item.value, 0);

  const segments = top.map((item, index) => ({
    id: item.id,
    label: item.label,
    value: item.value,
    color: DS_CHART_COLORS[index % DS_CHART_COLORS.length],
  }));
  if (restValue > 0) {
    segments.push({
      id: '__other',
      label: `Other (${rest.length})`,
      value: restValue,
      color: OTHER_COLOR,
    });
  }

  const donutData = segments.map((s) => ({
    v: total > 0 ? (s.value / total) * 100 : 0,
    c: s.color,
    l: s.label,
    tv: formatCurrency(s.value),
  }));

  const legend = segments.map((s) => ({
    id: s.id,
    color: s.color,
    name: s.label,
    value: formatCurrencyNoUnit(s.value),
  }));

  // Size donut from the card: leave room for the legend on the same row.
  // Cap near CHART_H so this card matches sibling Top10 bar charts.
  const compact = cardW > 0 && cardW < 520;
  const donutSize =
    cardW > 0
      ? Math.max(120, Math.min(CHART_H, Math.round(cardW * (compact ? 0.38 : 0.42))))
      : 164;

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        minHeight: CHART_H,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        overflow: 'hidden',
        p: 0.5,
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: compact ? 1.5 : 2.5,
          width: '100%',
          maxHeight: CHART_H,
        }}>
        <Box sx={{ width: donutSize, height: donutSize, flex: 'none' }}>
          <SvgDonut
            data={donutData}
            centerTop="Total"
            centerBot={formatCurrencyNoUnit(total)}
            size={donutSize}
          />
        </Box>
        <DonutLegend items={legend} compact={compact || segments.length > 6} />
      </Box>
    </Box>
  );
}
